// Section Navigation Module
import { getEditor } from './editor.js';
import { getCurrentMeta } from './files.js';

let navBar = null;
let navContainer = null;
let editorScrollContainer = null;
let sections = [];

// Map of section names to display labels
const SECTION_MAP = {
  'REQUIREMENTS': 'Reqs',
  'DESIGN SPECIFICATIONS': 'Design specs',
  'CMS BEHAVIOUR': 'CMS',
  'DEVELOPMENT GUIDANCE': 'Dev guidance',
  'DOCUMENTATION GUIDANCE': 'Docs',
  'COMPLETION CHECKLIST': 'Checklist'
};

// Map section headings to meta keys
const SECTION_TO_META_KEY = {
  'REQUIREMENTS': 'requirements',
  'DESIGN SPECIFICATIONS': 'design_specs',
  'CMS BEHAVIOUR': 'cms',
  'DEVELOPMENT GUIDANCE': 'dev_guidance',
  'DOCUMENTATION GUIDANCE': 'docs',
  'COMPLETION CHECKLIST': 'checklist'
};

// Pre-dev sections that must be complete before v1.0 milestone
const PRE_DEV_SECTIONS = [
  'requirements',
  'design_specs',
  'a11y_approval',
  'cms',
  'dev_guidance'
];

export function initSectionNav() {
  const DEBUG = window.DEBUG_FILE_OPEN || false;
  
  console.log('Section nav init called');
  console.log('🧭 Initializing section navigation...');
  
  if (DEBUG) console.log('[DEBUG] [section-nav] initSectionNav called');
  
  // Get DOM elements
  navBar = document.getElementById('sectionNavBar');
  navContainer = document.getElementById('sectionNav');
  editorScrollContainer = document.querySelector('.editor');
  
  console.log('Nav bar:', navBar);
  console.log('Nav container:', navContainer);
  console.log('Editor element:', editorScrollContainer);
  
  if (!navBar || !navContainer) {
    console.error('❌ Section nav elements not found');
    return;
  }
  
  if (!editorScrollContainer) {
    console.error('❌ Editor scroll container not found');
    return;
  }
  
  // Hide initially
  navBar.style.display = 'none';
  
  // Attach scroll listener
  editorScrollContainer.addEventListener('scroll', handleScroll);
  
  console.log('✅ Section navigation initialized');
}

export function updateSectionNav() {
  console.log('Update section nav called');
  if (!navContainer) return;
  
  const editor = getEditor();
  if (!editor) return;
  
  // Find all H1 elements in the ProseMirror editor
  const proseMirrorEl = document.querySelector('.ProseMirror');
  if (!proseMirrorEl) return;
  
  const h1Elements = proseMirrorEl.querySelectorAll('h1');
  console.log('Found H2s:', h1Elements.length);
  
  // Parse and match sections
  sections = [];
  h1Elements.forEach(h1 => {
    const text = h1.textContent.trim().toUpperCase();
    
    // Check if this heading matches one of our known sections
    for (const [key, label] of Object.entries(SECTION_MAP)) {
      if (text === key) {
        sections.push({
          key: key,
          label: label,
          element: h1
        });
        break;
      }
    }
  });
  
  console.log('Matched sections:', sections);
  console.log('🧭 Found sections:', sections.length, sections.map(s => s.label));
  
  const DEBUG = window.DEBUG_FILE_OPEN || false;
  
  // Only show nav if 2+ sections found
  if (sections.length >= 2) {
    if (DEBUG) console.log('[DEBUG] [section-nav] Showing nav bar (2+ sections found)');
    renderNav();
    navBar.style.display = 'flex';
    if (DEBUG) console.log('[DEBUG] [section-nav] navBar.style.display set to flex');
  } else {
    if (DEBUG) console.log('[DEBUG] [section-nav] Hiding nav bar (< 2 sections)');
  const DEBUG = window.DEBUG_FILE_OPEN || false;
  
  if (DEBUG) console.log('[DEBUG] [section-nav] clearSectionNav called');
  
    navBar.style.display = 'none';
  }
  
  if (DEBUG) {
    const ec = document.getElementById('editorContainer');
    const ea = document.getElementById('editorArea');
    console.log('[DEBUG] [section-nav] After updateSectionNav - editorContainer display:', ec ? window.getComputedStyle(ec).display : 'NOT FOUND');
    console.log('[DEBUG] [section-nav] After updateSectionNav - editorArea display:', ea ? window.getComputedStyle(ea).display : 'NOT FOUND');
  }
}

export function clearSectionNav() {
  if (!navBar) return;
  
  sections = [];
  navContainer.innerHTML = '';
  navBar.style.display = 'none';
  
  console.log('🧭 Section navigation cleared');
}

function renderNav() {
  if (!navContainer) return;
  
  // Clear existing buttons
  navContainer.innerHTML = '';
  
  // Create button for each section
  sections.forEach((section, index) => {
    const button = document.createElement('button');
    button.className = 'section-nav-item';
    button.textContent = section.label;
    button.dataset.sectionKey = section.key;
    
    // Add complete class if section is marked complete in meta
    const meta = getCurrentMeta();
    const metaKey = SECTION_TO_META_KEY ? SECTION_TO_META_KEY[section.key] : 'NO MAPPING';
    const sectionStatus = meta?.sections?.[metaKey];
    
    // Debug logging
    console.log('🎨 Pill render:', { 
      sectionKey: section.key, 
      metaKey, 
      sectionStatus, 
      hasMeta: !!meta,
      metaSections: meta?.sections,
      willAddCompleteClass: meta && metaKey && meta.sections[metaKey] === 'complete'
    });
    
    if (meta && metaKey && meta.sections[metaKey] === 'complete') {
      button.classList.add('complete');
      console.log(`✅ Added 'complete' class to ${section.key} pill`);
    } else {
      console.log(`⚪ Did NOT add 'complete' class to ${section.key} pill`);
    }
    
    // Click handler - smooth scroll to heading
    button.addEventListener('click', () => {
      scrollToSection(section);
    });
    
    navContainer.appendChild(button);
  });
  
  // Add milestone button (v1.0 Ready for dev)
  const meta = getCurrentMeta();
  if (meta) {
    const milestoneContainer = document.createElement('div');
    milestoneContainer.className = 'milestone-button-container';
    milestoneContainer.id = 'milestoneButton';
    
    const button = document.createElement('button');
    button.id = 'readyForDevButton';
    button.className = 'milestone-button';
    button.textContent = 'Ready for dev';
    
    milestoneContainer.appendChild(button);
    navContainer.appendChild(milestoneContainer);
    
    // Update initial state
    updateMilestoneButton();
    
    // Handle button click
    button.addEventListener('click', handleMilestoneClick);
  }
  
  // Update active state
  updateActiveSection();
}

function scrollToSection(section) {
  if (!section.element || !editorScrollContainer) return;
  
  // Get position relative to scroll container
  const editorRect = editorScrollContainer.getBoundingClientRect();
  const sectionRect = section.element.getBoundingClientRect();
  const scrollTop = editorScrollContainer.scrollTop;
  
  // Calculate target scroll position (with small offset from top)
  const targetScroll = scrollTop + (sectionRect.top - editorRect.top) - 20;
  
  // Smooth scroll
  editorScrollContainer.scrollTo({
    top: targetScroll,
    behavior: 'smooth'
  });
}

function handleScroll() {
  updateActiveSection();
}

function updateActiveSection() {
  if (!navContainer || !editorScrollContainer || sections.length === 0) return;
  
  const scrollTop = editorScrollContainer.scrollTop;
  const editorRect = editorScrollContainer.getBoundingClientRect();
  const threshold = 100; // px from top of editor
  
  // Find which section is currently in view
  let activeIndex = 0;
  
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    if (!section.element) continue;
    
    const sectionRect = section.element.getBoundingClientRect();
    const relativeTop = sectionRect.top - editorRect.top;
    
    // If section is past the threshold, it's the active one
    if (relativeTop <= threshold) {
      activeIndex = i;
    } else {
      break;
    }
  }
  
  // Update button states
  const buttons = navContainer.querySelectorAll('.section-nav-item');
  buttons.forEach((button, index) => {
    if (index === activeIndex) {
      button.classList.add('active');
    } else {
      button.classList.remove('active');
    }
  });
}

// Update completion status styling without rebuilding nav
export function updateSectionNavCompletion() {
  if (!navContainer) return;
  
  const meta = getCurrentMeta();
  if (!meta) return;
  
  console.log('🔄 updateSectionNavCompletion called', { meta });
  
  const buttons = navContainer.querySelectorAll('.section-nav-item');
  buttons.forEach(button => {
    if (!button?.dataset?.sectionKey) return;
    const sectionKey = button.dataset.sectionKey;
    const metaKey = SECTION_TO_META_KEY[sectionKey];
    
    console.log('🔄 Updating pill:', { sectionKey, metaKey, status: meta.sections[metaKey] });
    
    if (metaKey && meta.sections[metaKey] === 'complete') {
      button.classList.add('complete');
      console.log(`✅ Added 'complete' class to ${sectionKey} pill (update)`);
    } else {
      button.classList.remove('complete');
      console.log(`⚪ Removed 'complete' class from ${sectionKey} pill (update)`);
    }
  });
  
  // Update milestone button state
  updateMilestoneButton();
}

// Update milestone button state based on section completion
function updateMilestoneButton() {
  const container = document.getElementById('milestoneButton');
  if (!container) return;
  
  const meta = getCurrentMeta();
  if (!meta) return;
  
  // Check if already at v1.0 or higher
  const [major] = meta.version.split('.').map(Number);
  const alreadyPublished = major >= 1;
  
  console.log('🚀 Milestone button state:', { alreadyPublished, version: meta.version });
  
  if (alreadyPublished) {
    // Show as static label - milestone reached
    container.classList.add('completed');
    container.classList.remove('disabled');
    container.title = 'v1.0 milestone reached';
    
    // Replace content with static label
    container.innerHTML = `
      <span class="milestone-icon">✓</span>
      <span class="milestone-label">Ready for dev</span>
    `;
  } else {
    // Show as interactive button
    container.classList.remove('completed');
    
    // Ensure button exists
    let button = container.querySelector('button');
    if (!button) {
      // Recreate button
      container.innerHTML = `
        <button id="readyForDevButton" class="milestone-button">Ready for dev</button>
      `;
      button = container.querySelector('button');
      // Re-attach event listener
      button.addEventListener('click', handleMilestoneClick);
    }
    
    // Always enable the button - let the user decide when they're ready
    button.disabled = false;
    container.classList.remove('disabled');
    container.title = 'Click to mark as ready for development (v1.0)';
  }
}

// Handle milestone button click - trigger publish with v1.0 bump
async function handleMilestoneClick(event) {
  const button = event.target;
  const meta = getCurrentMeta();
  
  if (!meta || button.disabled) {
    return;
  }
  
  try {
    console.log('🚀 Triggering v1.0 milestone publish...');
    
    // Get the publish button and set milestone flag as data attribute
    const publishBtn = document.getElementById('publishBtn');
    publishBtn.dataset.milestonePublish = 'true';
    if (publishBtn) {
      // Update publish button text to show v1.0
      publishBtn.innerHTML = '<i class="ph ph-rocket-launch"></i> Publish v1.0.0';
      
      // Trigger the publish modal
      publishBtn.click();
      
      // The flag will be consumed by the publishVersion function
      // and then cleared
    } else {
      const filesModule = await import('./files.js');
      const { showToast } = filesModule;
      showToast('Unable to trigger publish - please use the publish button manually', 'error');
    }
  } catch (error) {
    console.error('Error handling milestone click:', error);
  }
}
